import React, { FCC, useState } from 'react'

import {
  DocumentHead,
  LoadingState,
  Pill,
  useForTablet,
  Tabs,
  Tab,
  TabList,
  TabPanel,
  ErrorMessage,
  DocumentHeader,
} from '@sketch/components'
import { DocumentItemsLayout } from '../../../shares/components/DocumentItemsLayout'
import { Footer } from '../../../shares/components/DocumentItemsLayout/DocumentItemsLayout.styles'

import {
  HeaderAfterCollections,
  DocumentsWrapper,
  TabPanelContent,
  MobileWrapper,
} from './ArchiveList.styles'

import {
  GetStashedStorageItemsDocument,
  GetStashedStorageItemsQuery,
  WorkspaceMinimalFragment,
  useGetDocumentListSettingsQuery,
} from '@sketch/gql-types'
import { ScrollToTop } from '../../../../components/ScrollToTopButton'
import NestedProjectItemsLayout from '../../../nestedProjects/components/NestedProjectItemsLayout'
import NestedProjectDropdown from '../../../nestedProjects/components/NestedProjectDropdown'
import { useGetStashedStorageItems } from '../../operations'
import ShareEmptyState from '../../../shares/components/ShareEmptyState'
import {
  LayoutPortal,
  useSearch,
  workspaceSidebarLayoutSymbols,
} from '@sketch/utils'
import { isProject, isShare } from '../../utils/filterTypes'
import { DocumentItemDropdown } from '../../../shares/components/DocumentItemDropdown'
import {
  isNestedProject,
  updatePinnedShares,
} from '../../../shares/components/ShareActionsDropdown/pinShare.cache'
import { PROJECTS_LAYOUT_COLUMNS } from '../../constants'

const INITIAL_TAB = 0
const STORAGE_DEFAULT_TYPES = ['SHARE' as const, 'PROJECT' as const]

const getTypesForProjectQuery = (
  isMobile: boolean,
  tabState: number | null
) => {
  // On desktop we always want to show both shares and projects because they are listed together
  // On mobile, the initial load should always fetch both given that we don't know how many shares and projects there are, or if there are any
  // and on that case we hide the tab bar (if there's only one type of item )
  if (!isMobile || tabState === null) {
    // Defaulting to all types (["SHARE", "PROJECT"]) on the BE
    return STORAGE_DEFAULT_TYPES
  }

  // After a tab is selected we already know there's more than one type of item
  // so we can return the selected type
  if (tabState === 0) {
    return ['PROJECT' as const]
  }

  return ['SHARE' as const]
}

export interface ArchiveListProps {
  workspace: WorkspaceMinimalFragment
}
export const ArchiveList: FCC<ArchiveListProps> = props => {
  const { workspace } = props
  const { searchDebounced: search } = useSearch()

  const [tabState, setTabState] = useState<number | null>(null)

  const isTabletAndBigger = useForTablet()
  const isMobile = !isTabletAndBigger

  const { identifier: workspaceId } = workspace

  const useStashStorageProps = {
    workspaceId,
    search,
    source: 'ARCHIVE' as const,
    types: STORAGE_DEFAULT_TYPES,
  }

  // The "initialQuery" will be used fetch the total count of storage items
  // and check (on mobile) if we should show the tab bar or not
  const initialQuery = useGetStashedStorageItems(useStashStorageProps)

  const result = useGetStashedStorageItems({
    ...useStashStorageProps,
    types: getTypesForProjectQuery(isMobile, tabState),
  })

  // Load the document layout
  const { data: documentListSettingsData } = useGetDocumentListSettingsQuery()
  const { documentsLayout } = documentListSettingsData || {}

  if (initialQuery.kind === 'loading') {
    return <LoadingState />
  }

  if (
    !result ||
    !initialQuery ||
    result.kind === 'error' ||
    initialQuery.kind === 'error'
  ) {
    return <ErrorMessage.Generic />
  }

  const { items: storageItems, loading } = result

  const nestedProjects = storageItems?.filter(isProject) || []
  const shares = storageItems?.filter(isShare) || []

  const { totalCounts } = initialQuery

  const hasShares = !!totalCounts?.shares
  const hasNestedProjects = !!totalCounts?.projects

  const documentHeader = isTabletAndBigger && (
    <>
      <DocumentHead title="Archive" />
      <LayoutPortal token={workspaceSidebarLayoutSymbols.header}>
        <DocumentHeader
          title="Archived Items"
          description="A place for documents or folders you’re not actively working on."
          learnMoreTooltipTitle="Learn more about archive"
          learnMoreTooltipURL="https://www.sketch.com/redirect/web/archive-projects"
        />
      </LayoutPortal>
      <LayoutPortal token={workspaceSidebarLayoutSymbols.headerSlim}>
        <DocumentHeader title="Archived Items" />
      </LayoutPortal>
    </>
  )

  // Project Empty State
  if (!loading && totalCounts.storageItems === 0) {
    return (
      <>
        {documentHeader}

        <ShareEmptyState
          search={search}
          workspace={workspace}
          filter="ARCHIVE_PROJECTS"
        />
      </>
    )
  }

  const renderDocumentsList = () => {
    if (!hasShares) {
      return null
    }

    const isGridLayout = documentsLayout === 'GRID'

    return (
      <DocumentsWrapper
        listLayout={!isGridLayout}
        // return list without gradient styling when there's no nested projects
        grouped={hasNestedProjects}
      >
        {/**
         * This header should only be seen when the user has both shares and
         * nested projects and is on the grid view
         */}
        {hasNestedProjects && isGridLayout && isTabletAndBigger && (
          <HeaderAfterCollections>
            Documents
            <Pill variant="secondary">{totalCounts.shares}</Pill>
          </HeaderAfterCollections>
        )}

        <DocumentItemsLayout
          workspaceId={workspace.identifier}
          shares={shares}
          totalCount={totalCounts.shares}
          onLoadMore={result?.fetchMore!}
          renderDropdown={share => {
            return (
              <DocumentItemDropdown
                share={share}
                workspaceIdentifier={workspace.identifier}
                workspace={workspace}
                pinDocumentConfig={{
                  shareIdentifier: share.identifier,
                  pinned: !!share?.pinnedByCurrentUserAt,
                  update(cache, { data }) {
                    if (!data) return

                    updatePinnedShares<GetStashedStorageItemsQuery>({
                      shareId: share.identifier,
                      pickPagination: data => data?.workspace.storageItems,
                      readData: () => {
                        return (
                          cache.readQuery<GetStashedStorageItemsQuery>({
                            query: GetStashedStorageItemsDocument,
                            variables: result.variables,
                          }) ?? undefined
                        )
                      },
                      writeData: newData => {
                        cache.writeQuery({
                          query: GetStashedStorageItemsDocument,
                          variables: result.variables,
                          data: newData,
                        })
                      },
                      sort: 'ARCHIVED_AT_DESC',
                    })
                  },
                }}
              />
            )
          }}
        />
      </DocumentsWrapper>
    )
  }

  const renderNestedProjectsList = () => {
    if (!hasNestedProjects) return null

    return (
      <>
        <NestedProjectItemsLayout
          workspaceId={workspaceId}
          nestedProjects={nestedProjects}
          onLoadMore={result?.fetchMore!}
          disabledDrag={!(workspace.userCanEdit || workspace.userCanAdminister)}
          totalCount={totalCounts.projects}
          customColumns={PROJECTS_LAYOUT_COLUMNS}
          renderDropdown={item => {
            if (!isNestedProject(item)) return

            return (
              <NestedProjectDropdown
                workspace={workspace}
                nestedProject={item}
              />
            )
          }}
        />
        <ScrollToTop />
      </>
    )
  }

  const title = 'Archived Items'

  // For mobile view we want to show tabs
  if (isMobile && hasNestedProjects) {
    return (
      <MobileWrapper>
        <DocumentHead title={title} />

        <Tabs
          selectedTabIndex={tabState || INITIAL_TAB}
          onTabSelected={setTabState}
        >
          <TabList>
            <Tab>
              Folders{' '}
              {totalCounts.projects && (
                <Pill variant="secondary">{totalCounts.projects}</Pill>
              )}
            </Tab>
            <Tab>
              Documents{' '}
              {totalCounts.shares && (
                <Pill variant="secondary">{totalCounts.shares}</Pill>
              )}
            </Tab>
          </TabList>
          <TabPanel>
            <TabPanelContent>
              {renderNestedProjectsList()}
              <Footer />
            </TabPanelContent>
          </TabPanel>
          <TabPanel>
            <TabPanelContent>{renderDocumentsList()}</TabPanelContent>
          </TabPanel>
        </Tabs>
      </MobileWrapper>
    )
  }

  return (
    <>
      {documentHeader}

      {renderNestedProjectsList()}
      {renderDocumentsList()}
    </>
  )
}
